import React, { useState } from 'react';
import {
  Network,
  Cpu,
  Lock,
  Zap,
  ShieldCheck,
  Server,
  Layers,
  Globe,
  Key,
  Radio,
  FileCode2,
  CheckCircle2,
  ArrowRight,
  GitMerge,
  Repeat
} from 'lucide-react';
import { Button } from '../components/ui/button';

const APIIntegration = () => {
  const [activeTab, setActiveTab] = useState('rest');

  const coreServices = [
    {
      icon: Cpu,
      title: 'Custom RESTful API Engineering',
      badge: 'High Throughput',
      color: 'from-cyan-500 to-blue-600',
      description: 'Clean, stateless, and versioned (v1/v2) REST APIs built with OpenAPI 3.0 (Swagger) specs for maximum inter-system compatibility.',
      bullets: ['OpenAPI 3.0 / Swagger Docs', 'Idempotent HTTP Endpoints', 'Automated Postman Test Suites']
    },
    {
      icon: GitMerge,
      title: 'GraphQL Schema & Apollo Server',
      badge: 'Zero Over-fetching',
      color: 'from-purple-500 to-indigo-600',
      description: 'Single-endpoint GraphQL schemas engineered with Apollo Server, custom resolvers, and real-time GraphQL Subscriptions.',
      bullets: ['Declarative Field Fetching', 'GraphQL Schema Stitching', 'Real-Time Subscriptions']
    },
    {
      icon: Lock,
      title: 'Enterprise API Gateway & Security',
      badge: 'OWASP Compliant',
      color: 'from-orange-500 to-amber-500',
      description: 'Hardened API Gateways (Kong / AWS API Gateway) with OAuth2, OpenID Connect, JWT tokens, and IP rate-limiting guards.',
      bullets: ['OAuth 2.0 & JWT Stateless Auth', 'Kong / AWS API Gateway Config', 'IP Rate Limiting & DDoS Shield']
    },
    {
      icon: Zap,
      title: 'Third-Party SaaS & Payment Integration',
      badge: 'Seamless Sync',
      color: 'from-emerald-500 to-teal-600',
      description: 'Plug-and-play connector pipelines for Stripe, PayPal, Salesforce, HubSpot, Twilio, SAP, and custom ERP databases.',
      bullets: ['Stripe & Razorpay Payment Flows', 'Salesforce & HubSpot CRM Sync', 'Twilio & WhatsApp Telemetry']
    },
    {
      icon: Radio,
      title: 'Real-Time WebSockets & Event Streams',
      badge: 'Event Driven',
      color: 'from-pink-500 to-rose-600',
      description: 'Low-latency WebSockets, Socket.io, and message queues (RabbitMQ, Apache Kafka) for instant live updates and webhook pushes.',
      bullets: ['Socket.io & WebSockets Engine', 'Kafka & RabbitMQ Messaging', 'Webhook Callback Security']
    },
    {
      icon: Repeat,
      title: 'Legacy SOAP & DB Modernization',
      badge: 'System Modernization',
      color: 'from-blue-500 to-cyan-600',
      description: 'Wrap legacy SOAP web services or raw SQL databases into secure, lightning-fast JSON RESTful or GraphQL endpoints.',
      bullets: ['SOAP-to-REST Wrapping', 'Legacy DB RESTification', 'Zero Downtime Migration']
    }
  ];

  const comparisonData = {
    rest: {
      title: 'RESTful Architecture',
      desc: 'Standardized, resource-oriented HTTP endpoints ideal for web caching, public APIs, and traditional CRUD applications.',
      advantages: [
        'Built-in HTTP Caching & CDN Support',
        'Universal industry adoption across all languages',
        'Strict HTTP Status Code semantics (200, 201, 400, 404)',
        'Easy third-party developer integration'
      ]
    },
    graphql: {
      title: 'GraphQL Architecture',
      desc: 'Flexible, query-driven schema language empowering client apps to request exact fields needed in a single round-trip.',
      advantages: [
        'Zero over-fetching or under-fetching of data',
        'Single endpoint (/graphql) handling complex queries',
        'Strongly typed schema with automatic introspection',
        'Real-time data pushes via GraphQL Subscriptions'
      ]
    }
  };

  const apiProcess = [
    { step: '01', title: 'API Specification & Schema Design', desc: 'Drafting OpenAPI 3.0 or GraphQL SDL schemas to define data models, endpoints, and error contracts upfront.' },
    { step: '02', title: 'Microservice Core Development', desc: 'Engineered in Node.js/Fastify, Python/FastAPI, or Go with optimized database queries and caching.' },
    { step: '03', title: 'Security & OAuth Gateway Wrap', desc: 'Configuring JWT token validation, rate-limiting rules, CORS policies, and TLS 1.3 encryption.' },
    { step: '04', title: 'Automated Postman & Integration Testing', desc: 'Simulating high-concurrency requests, edge cases, and automated regression test suites.' },
    { step: '05', title: 'Interactive Documentation & Studio Handoff', desc: 'Publishing live Swagger / Apollo Studio portals for instant developer consumption and SDK generation.' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20">

      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 via-slate-950 to-gray-950 relative overflow-hidden border-b border-gray-800/80">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">

            <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-6">
              <Network className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                Enterprise API Engineering
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              Secure RESTful & <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">GraphQL Architectures</span>
            </h1>

            <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-10 max-w-3xl mx-auto">
              Connect systems seamlessly with high-performance microservice APIs, single-endpoint GraphQL schemas, API gateways, and enterprise third-party integrations built for zero downtime.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a href="/contact">
                <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-cyan-500/30 transition-all duration-300 flex items-center gap-2">
                  Consult API Architects
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
              <a href="#services">
                <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                  Explore Capabilities
                </Button>
              </a>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Services Grid */}
      <section id="services" className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Full-Lifecycle API Solutions
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Comprehensive <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">API Capabilities</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              From greenfield GraphQL schemas to complex third-party SaaS integrations, we build secure, scalable data pipelines.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreServices.map((service, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-cyan-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full">
                      {service.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {service.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive REST vs GraphQL Comparison Tab */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Architectural Paradigms: REST vs. GraphQL
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-8">
              We help you choose or combine the ideal API pattern based on your application's data complexity and client requirements.
            </p>

            <div className="inline-flex p-1 bg-gray-900 border border-gray-800 rounded-xl space-x-2">
              <button
                onClick={() => setActiveTab('rest')}
                className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'rest'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                RESTful APIs
              </button>
              <button
                onClick={() => setActiveTab('graphql')}
                className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'graphql'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                GraphQL Schemas
              </button>
            </div>
          </div>

          <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-3">
              {comparisonData[activeTab].title}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-8">
              {comparisonData[activeTab].desc}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {comparisonData[activeTab].advantages.map((adv, idx) => (
                <div key={idx} className="bg-gray-950/80 border border-gray-800 rounded-xl p-4 flex items-center space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-200">{adv}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* API Lifecycle Process */}
      <section className="py-24 bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
              Our API Engineering Lifecycle
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Design-first OpenAPI and GraphQL schema workflows ensuring predictable integration and zero downtime.
            </p>
          </div>

          <div className="space-y-6">
            {apiProcess.map((proc, idx) => (
              <div key={idx} className="bg-gray-900/80 border border-gray-800 rounded-2xl p-6 flex flex-col md:flex-row items-start md:items-center gap-6 hover:border-cyan-500/50 transition-all">
                <span className="text-3xl font-black text-cyan-400 font-mono flex-shrink-0">{proc.step}</span>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">{proc.title}</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">{proc.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CTA Box */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Need Custom API Gateways or GraphQL Integration?
          </h2>
          <p className="text-gray-300 text-base mb-8 max-w-2xl mx-auto">
            Talk to our API engineering architects to design high-throughput, secure REST and GraphQL backends for your ecosystem.
          </p>
          <a href="/contact">
            <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg">
              Consult API Engineers
            </Button>
          </a>
        </div>
      </section>

    </div>
  );
};

export default APIIntegration;
